import { IFormAssignmentCallback } from "./IFormAssignmentCallback";
import { AppGlueCorePlugins } from "../../AppGlueCorePlugins";
import { ChannelTask } from "./ChannelTask";
import { AssignmentConstants } from "./AssignmentConstants";
import { IUserInfo } from "../DataStorage/IUserInfo";
import { IFormAssignmentPlugin } from "./IFormAssignmentPlugin";
import { ChannelTaskAssignment } from "./ChannelTaskAssignment";
import { AppGlueError } from "../../AppGlueError";
import { FlowExecutionData } from "../../Flow/Engine/FlowExecutionData";
import { ValidationIssue } from "../../Common/IDesignValidationProvider";
import { ChannelMessage } from "./ChannelMessage";
import { ChannelMembership } from "./ChannelMembership";
import { DataStoreQueryCondition, DataStoreQueryConditionType } from "../DataStorage/DataStoreQueryCondition";

export class FormAssignmentPlugin implements IFormAssignmentPlugin {
    callback?: IFormAssignmentCallback;
    dataSource = AppGlueCorePlugins.DataStoreFactory?.getDataStoreByName<ChannelTask>(
        AssignmentConstants.COLLECTION_NAME,
    );
    channelDataSource = AppGlueCorePlugins.DataStoreFactory?.getDataStoreByName<ChannelTask>(
        AssignmentConstants.COLLECTION_NAME,
    );
    channelMembershipSource = AppGlueCorePlugins.DataStoreFactory?.getDataStoreByName<ChannelMembership>(
        AssignmentConstants.COLLECTION_NAME,
    );
    channelMesageSource = AppGlueCorePlugins.DataStoreFactory?.getDataStoreByName<ChannelMessage>(
        AssignmentConstants.COLLECTION_NAME,
    );

    async addAssignmentHistory(user: IUserInfo, assignmentId: string, notes: string): Promise<ChannelTask> {
        // not implemented
        let ass = await this.getAssignment(user, assignmentId);

        let message = new ChannelMessage(notes);
        message.channelName = ass.channelName;
        message.parentId = ass.id!;
        message.createdOn = new Date();
        message.createdBy = user.userEmail;
        message.teamName = user.teamName;

        await this.channelMesageSource?.store(user, message);

        this.callback?.assignmentHistoryAdded(ass);
        return ass;
    }

    async getAssignmentHistory(user: IUserInfo, assignmentId: string): Promise<ChannelMessage[]> {
        let m = await this.channelMesageSource?.query(
            user,
            new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "parentId", assignmentId),
        );
        return m ?? [];
    }

    async addUserAssignment(user: IUserInfo, assignmentId: string, userEmail: string): Promise<ChannelTask> {
        let ass = await this.getAssignment(user, assignmentId);

        let stateAssignment = ass.assignments.find((a) => {
            return a.state === AssignmentConstants.OPEN_STATE;
        });

        if (!stateAssignment) {
            stateAssignment = new ChannelTaskAssignment(AssignmentConstants.OPEN_STATE);
            ass.assignments.push(stateAssignment);
        }

        if (stateAssignment.assignee.indexOf(userEmail) === -1) {
            stateAssignment.assignee.push(userEmail);
        }

        this.callback?.assignmentChanged(ass);

        await this.storeAssignmentInternal(user, ass);

        return ass;
    }

    async completeAssignment(user: IUserInfo, assignmentId: string, notes?: string): Promise<ChannelTask> {
        let ass = await this.getAssignment(user, assignmentId);

        ass.state = AssignmentConstants.COMPLETED_STATE;

        await this.addAssignmentHistory(user, assignmentId, `Completed: ${notes}`);

        await this.storeAssignmentInternal(user, ass);

        return ass;
    }

    async createAssignment(user: IUserInfo, data: ChannelTask, flowData: FlowExecutionData): Promise<ChannelTask> {
        this.callback?.assignmentCreated(data);
        return this.storeAssignment(user, data, flowData);
    }

    async getAllChannels(_user: IUserInfo, _filterForMyChannels: boolean): Promise<string[]> {
        return ["Tasks"];
    }

    async getUsersInChannel(_user: IUserInfo, _channelName: string): Promise<string[]> {
        return [];
    }

    async getAllUsers(user: IUserInfo): Promise<string[]> {
        return [user.userEmail];
    }

    async getUserCount(_user: IUserInfo): Promise<number> {
        return 1;
    }

    async searchUsers(user: IUserInfo, _searchString: string): Promise<string[]> {
        return [user.userEmail];
    }

    async validateChannelName(_user: IUserInfo, _channelName: string): Promise<ValidationIssue | undefined> {
        return undefined;
    }

    async validateUserInChannel(
        _user: IUserInfo,
        _channelName: string,
        _userEmail: string,
    ): Promise<ValidationIssue | undefined> {
        return undefined;
    }

    async validateUser(_user: IUserInfo, _userEmail: string): Promise<ValidationIssue | undefined> {
        return undefined;
    }

    async isAssignedToMe(user: IUserInfo, assignmentId: string): Promise<boolean> {
        let ass = await this.getAssignment(user, assignmentId);

        let stateAssignment = ass.assignments.find((a) => {
            return a.state === AssignmentConstants.OPEN_STATE;
        });

        if (stateAssignment) {
            if (stateAssignment.assignee.indexOf(user.userEmail) !== -1) {
                return true;
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    async getAllPriorities(_user: IUserInfo): Promise<string[]> {
        return AssignmentConstants.DEFAULT_PRIORITIES;
    }

    async getAssignment(user: IUserInfo, id: string): Promise<ChannelTask> {
        return await this.dataSource?.get(user, id)!;
    }

    async removeUserAssignment(user: IUserInfo, assignmentId: string, userEmail: string): Promise<ChannelTask> {
        let ass = await this.getAssignment(user, assignmentId);

        let stateAssignment = ass.assignments.find((a) => {
            return a.state === AssignmentConstants.OPEN_STATE;
        });

        if (stateAssignment) {
            if (stateAssignment.assignee.indexOf(userEmail) !== -1) {
                stateAssignment.assignee = stateAssignment.assignee.filter((a) => {
                    return a !== userEmail;
                });
            }
        }

        this.callback?.assignmentChanged(ass);

        await this.storeAssignmentInternal(user, ass);

        return ass;
    }

    async storeAssignmentInternal(user: IUserInfo, data: ChannelTask): Promise<ChannelTask> {
        this.callback?.assignmentChanged(data);

        if (!data.flowId) throw new AppGlueError("Flow id not supplied");
        if (!data.flowStepId) throw new AppGlueError("Step id not supplied");
        if (!data.flowExecutionId) throw new AppGlueError("Flow execution id not supplied");
        if (!data.flowStepExecutionId) throw new AppGlueError("Step execution id not supplied");

        if (!data.taskName) throw new AppGlueError("Task name not supplied");
        if (!data.flowName) throw new AppGlueError("Flow name not supplied");
        if (!data.stepName) throw new AppGlueError("Step name not supplied");
        if (!data.sequenceName) throw new AppGlueError("Sequence name not supplied");
        if (!data.overideActionName) throw new AppGlueError("Action name not supplied");

        if (!data.channelName) throw new AppGlueError("Channel name not supplied");

        if (!data.state) data.state = AssignmentConstants.OPEN_STATE;

        if (!data.assignments || data.assignments.length === 0)
            data.assignments.push(new ChannelTaskAssignment(AssignmentConstants.OPEN_STATE));

        let ass = data.assignments.find((s) => {
            return s.state === AssignmentConstants.OPEN_STATE;
        });

        data.currentAssignments = ass!.assignee;

        return await this.dataSource?.store(user, data)!;
    }

    async storeAssignment(user: IUserInfo, data: ChannelTask, flowData: FlowExecutionData): Promise<ChannelTask> {
        if (!flowData) throw new AppGlueError("Flow data not supplied");
        // store the flow data
        await AppGlueCorePlugins.FlowExecutionPlugin?.saveFlowData(user, flowData);

        return this.storeAssignmentInternal(user, data);
    }

    async getDirectFormURL(_user: IUserInfo, _assignmentId: string): Promise<string> {
        throw new AppGlueError("Must be implemented in server environment");
    }

    async getDirectAssignmentURL(_user: IUserInfo, _assignmentId: string): Promise<string> {
        throw new AppGlueError("Must be implemented in server environment");
    }
}
